import { useCallback, useEffect, useState } from "react";
import { Check, ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "@/lib/utils";

// Items carry "<name>\u0000<id>" as their cmdk value: ids keep two orgs with the same name apart, and the
// filter below only ever matches the name, so typing "a" does not match every id with an "a" in it.
const SEP = "\u0000";

// Plain substring on the name rather than cmdk's fuzzy scoring: people type the start of an org's name
// and expect exactly the orgs containing it, in the order they already know.
function filterByName(value: string, search: string) {
  const name = value.split(SEP)[0] ?? "";
  return name.toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0;
}

function useScrollHints() {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [moreAbove, setMoreAbove] = useState(false);
  const [moreBelow, setMoreBelow] = useState(false);

  const check = useCallback(() => {
    if (!el) return;
    setMoreAbove(el.scrollTop > 4);
    setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, [el]);

  useEffect(() => {
    if (!el) return;
    check();
    el.addEventListener("scroll", check, { passive: true });
    const observer = new ResizeObserver(check);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", check);
      observer.disconnect();
    };
  }, [el, check]);

  const scrollBy = (direction: 1 | -1) =>
    el?.scrollBy({ top: direction * el.clientHeight * 0.8, behavior: "smooth" });

  return { ref: setEl, moreAbove, moreBelow, scrollBy };
}

/** Switching reloads the page so no data from the previous organisation survives in memory. */
export function OrgSwitcher() {
  const { user, activeOrganisationId, switchOrganisation } = useAuth();
  const [open, setOpen] = useState(false);
  const { ref: listRef, moreAbove, moreBelow, scrollBy } = useScrollHints();
  const orgs = user?.organisations ?? [];
  const activeName = orgs.find((o) => o.organisationId === activeOrganisationId)?.organisationName;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label="Switch organisation"
          className="hidden gap-1.5 sm:inline-flex"
        >
          <span className="max-w-[160px] truncate text-xs">{activeName ?? "Select org"}</span>
          <ChevronsUpDown className="size-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <Command filter={filterByName}>
          <CommandInput placeholder="Find an organisation..." />
          <div className="relative">
            <CommandList ref={listRef}>
              <CommandEmpty>No organisation matches.</CommandEmpty>
              <CommandGroup>
                {orgs.map((org) => {
                  const active = org.organisationId === activeOrganisationId;
                  return (
                    <CommandItem
                      key={org.organisationId}
                      value={`${org.organisationName}${SEP}${org.organisationId}`}
                      onSelect={() => {
                        setOpen(false);
                        if (active) return;
                        switchOrganisation(org.organisationId);
                        window.location.reload();
                      }}
                    >
                      <Check className={cn("size-4", active ? "opacity-100" : "opacity-0")} />
                      <span className="truncate text-sm">{org.organisationName}</span>
                      {org.clientType === "Demo" ? (
                        <span className="shrink-0 rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 text-[10px] font-semibold text-amber-700 uppercase dark:text-amber-400">
                          Demo
                        </span>
                      ) : null}
                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">{org.role}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
            {moreAbove && (
              <div className="pointer-events-none absolute inset-x-0 top-0 flex h-10 items-start justify-center bg-linear-to-b from-popover via-popover/80 to-transparent pt-1">
                <button
                  type="button"
                  aria-label="Scroll to see earlier organisations"
                  onClick={() => scrollBy(-1)}
                  className="pointer-events-auto flex size-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <ChevronUp className="size-4" />
                </button>
              </div>
            )}
            {moreBelow && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-10 items-end justify-center bg-linear-to-t from-popover via-popover/80 to-transparent pb-1">
                <button
                  type="button"
                  aria-label="Scroll to see more organisations"
                  onClick={() => scrollBy(1)}
                  className="pointer-events-auto flex size-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <ChevronDown className="size-4" />
                </button>
              </div>
            )}
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
